import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import api from '../utils/axios';
import {
  BarChart3, TrendingUp, AlertTriangle, Shield, Users, Clock,
  FileText, Lock, Mail, MailX, Download, Loader2, CheckCircle, XCircle
} from 'lucide-react';

const CasosDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const dashboardRef = useRef(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await api.get('/stats/casos-dashboard');
      setStats(res.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { default: jsPDF } = await import('jspdf');
      
      const element = dashboardRef.current;
      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.setFontSize(16);
      pdf.text('Informe de Gestion de Casos - AlertGuard', 10, 12);
      pdf.setFontSize(9);
      pdf.text(`Generado: ${new Date().toLocaleString('es-AR')}`, 10, 18);
      
      let yPos = 22;
      if (imgHeight + yPos > pdfHeight) {
        const ratio = (pdfHeight - yPos - 5) / imgHeight;
        pdf.addImage(imgData, 'PNG', 10, yPos, imgWidth * ratio, (pdfHeight - yPos - 5));
      } else {
        pdf.addImage(imgData, 'PNG', 10, yPos, imgWidth, imgHeight);
      }
      
      pdf.save(`Informe_Casos_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exportando PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (!stats) return null;

  const { resumen, por_estado, por_score, tendencia_7_dias, top_tipos_alerta, top_clientes_riesgo, actividad_30_dias, solicitudes, bloqueos } = stats;

  const estadoColors = {
    'ABIERTO': 'bg-blue-500',
    'EN_INVESTIGACION': 'bg-yellow-500',
    'EN_REVISION': 'bg-indigo-500',
    'ESCALADO': 'bg-red-500',
    'PENDIENTE': 'bg-slate-400',
    'CERRADO': 'bg-green-500',
    'RESUELTO': 'bg-emerald-500',
    'ARCHIVADO': 'bg-slate-300',
    'TESTING': 'bg-purple-500',
    'CANCELADO': 'bg-red-300',
    'ESPERANDO_COMPLIANCE': 'bg-amber-500',
  };

  const maxEstado = Math.max(...Object.values(por_estado || {}), 1);

  return (
    <div className="space-y-6">
      {/* Header with export */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Dashboard de Casos</h2>
          <p className="text-sm text-slate-500">Vista general de la gestion de casos, solicitudes y bloqueos</p>
        </div>
        <Button
          onClick={handleExportPDF}
          disabled={exporting}
          variant="outline"
          className="flex items-center gap-2"
          data-testid="export-dashboard-pdf"
        >
          {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          {exporting ? 'Generando...' : 'Exportar Informe'}
        </Button>
      </div>

      <div ref={dashboardRef} className="space-y-6">
        {/* Top KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 size={16} className="text-blue-600" />
              <span className="text-xs text-blue-600 font-medium">Total Casos</span>
            </div>
            <p className="text-2xl font-bold text-blue-800">{resumen?.total_casos || 0}</p>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={16} className="text-amber-600" />
              <span className="text-xs text-amber-600 font-medium">Abiertos</span>
            </div>
            <p className="text-2xl font-bold text-amber-800">{resumen?.casos_abiertos || 0}</p>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <div className="flex items-center gap-2 mb-1">
              <Shield size={16} className="text-indigo-600" />
              <span className="text-xs text-indigo-600 font-medium">En Investigacion</span>
            </div>
            <p className="text-2xl font-bold text-indigo-800">{resumen?.casos_en_investigacion || 0}</p>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle size={16} className="text-green-600" />
              <span className="text-xs text-green-600 font-medium">Cerrados</span>
            </div>
            <p className="text-2xl font-bold text-green-800">{resumen?.casos_cerrados || 0}</p>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center gap-2 mb-1">
              <FileText size={16} className="text-purple-600" />
              <span className="text-xs text-purple-600 font-medium">Solicitudes</span>
            </div>
            <p className="text-2xl font-bold text-purple-800">{resumen?.total_solicitudes || 0}</p>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <div className="flex items-center gap-2 mb-1">
              <Lock size={16} className="text-red-600" />
              <span className="text-xs text-red-600 font-medium">Cuentas Bloqueadas</span>
            </div>
            <p className="text-2xl font-bold text-red-800">{bloqueos?.cuentas_bloqueadas || 0}</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Casos por estado */}
          <Card className="p-5 lg:col-span-1">
            <h3 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
              <BarChart3 size={16} /> Casos por Estado
            </h3>
            <div className="space-y-2">
              {Object.entries(por_estado || {}).sort((a, b) => b[1] - a[1]).map(([estado, count]) => (
                <div key={estado} className="flex items-center gap-2">
                  <span className="text-xs text-slate-600 w-36 truncate">{estado.replace(/_/g, ' ')}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${estadoColors[estado] || 'bg-slate-400'} flex items-center justify-end pr-1.5`}
                      style={{ width: `${Math.max((count / maxEstado) * 100, 8)}%` }}
                    >
                      <span className="text-[10px] font-bold text-white">{count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Solicitudes */}
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
              <FileText size={16} /> Solicitudes
            </h3>
            {solicitudes ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-700">{solicitudes.total || 0}</p>
                    <p className="text-xs text-purple-600">Total</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">{solicitudes.recientes_30d || 0}</p>
                    <p className="text-xs text-blue-600">Ultimos 30 dias</p>
                  </div>
                </div>

                {/* Por estado */}
                <div>
                  <p className="text-xs text-slate-500 mb-1 font-medium">Por Estado:</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(solicitudes.por_estado || {}).map(([est, cnt]) => (
                      <span key={est} className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                        {est}: {cnt}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Por tipo */}
                {solicitudes.por_tipo?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1 font-medium">Por Tipo:</p>
                    <div className="space-y-1">
                      {solicitudes.por_tipo.slice(0, 5).map((t) => (
                        <div key={t.tipo} className="flex items-center justify-between text-xs">
                          <span className="text-slate-600 truncate">{t.tipo}</span>
                          <span className="font-medium text-slate-800">{t.cantidad}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Email stats */}
                <div className="flex items-center gap-3 pt-2 border-t">
                  <div className="flex items-center gap-1 text-xs">
                    <Mail size={12} className="text-green-600" />
                    <span className="text-green-700">{solicitudes.email?.enviados || 0} enviados</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <Clock size={12} className="text-amber-500" />
                    <span className="text-amber-600">{solicitudes.email?.pendientes || 0} pendientes</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <MailX size={12} className="text-red-500" />
                    <span className="text-red-600">{solicitudes.email?.errores || 0} errores</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">Sin datos de solicitudes</p>
            )}
          </Card>

          {/* Bloqueos */}
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
              <Lock size={16} /> Bloqueos de Cuenta
            </h3>
            {bloqueos ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-700">{bloqueos.cuentas_bloqueadas || 0}</p>
                    <p className="text-xs text-red-600">Bloqueadas</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-700">{bloqueos.bloqueos_totales || 0}</p>
                    <p className="text-xs text-orange-600">Total Ejecutados</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-700">{bloqueos.bloqueos_30d || 0}</p>
                    <p className="text-xs text-amber-600">Ultimos 30d</p>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-2">Resumen de acciones</p>
                  <div className="space-y-1 text-xs text-slate-600">
                    <div className="flex justify-between">
                      <span>Bloqueos exitosos</span>
                      <span className="font-medium">{bloqueos.bloqueos_totales || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Casos con cuenta bloqueada</span>
                      <span className="font-medium">{bloqueos.cuentas_bloqueadas || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">Sin datos de bloqueos</p>
            )}
          </Card>
        </div>

        {/* Bottom section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Tendencia 7 días */}
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
              <TrendingUp size={16} /> Tendencia - 7 dias
            </h3>
            <div className="space-y-2">
              {(tendencia_7_dias || []).map((d) => (
                <div key={d.fecha} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-20">{d.fecha?.slice(5)}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${Math.max((d.casos / Math.max(...tendencia_7_dias.map(t => t.casos), 1)) * 100, 5)}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-slate-700 w-6 text-right">{d.casos}</span>
                </div>
              ))}
              {(!tendencia_7_dias || tendencia_7_dias.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">Sin datos de tendencia</p>
              )}
            </div>
          </Card>

          {/* Top tipos de alerta */}
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
              <AlertTriangle size={16} /> Top Tipos de Alerta
            </h3>
            <div className="space-y-2">
              {(top_tipos_alerta || []).map((t) => (
                <div key={t.tipo} className="flex items-center justify-between text-xs border-b border-slate-100 pb-1">
                  <span className="text-slate-600 truncate flex-1">{t.tipo}</span>
                  <span className="font-bold text-slate-800 ml-2">{t.cantidad}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Top clientes riesgo */}
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-slate-700 mb-4 flex items-center gap-2">
              <Users size={16} /> Top Clientes en Riesgo
            </h3>
            <div className="space-y-2">
              {(top_clientes_riesgo || []).map((c) => (
                <div key={c.cuit} className="flex items-center justify-between text-xs border-b border-slate-100 pb-1">
                  <span className="text-slate-600 font-mono truncate flex-1">{c.cuit}</span>
                  <span className="text-slate-500 mx-2">{c.casos} casos</span>
                  <span className={`font-medium ${c.score_max >= 60 ? 'text-red-600' : c.score_max >= 40 ? 'text-orange-600' : 'text-slate-600'}`}>
                    score: {c.score_max || 0}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Activity summary */}
        <Card className="p-4 bg-slate-50">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Ultimos 30 dias: {actividad_30_dias?.casos_nuevos || 0} casos nuevos, {actividad_30_dias?.casos_actualizados || 0} actualizados</span>
            <span>Alertas en casos: {resumen?.total_alertas_en_casos || 0} | Promedio: {resumen?.promedio_alertas_por_caso || 0} alertas/caso</span>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CasosDashboard;
